<script setup>
// VibeVox: управление двусторонним переводом в окне ответа.
//  • Тумблер «Перевод вкл/выкл» (стиль как «Ответить/Личная заметка»: серый трек + белый чип).
//  • Поиск-пикер языка ОПЕРАТОРА (на который переводим входящие). 108 языков, дефолт = язык интерфейса.
// Исходящие переводятся авто на язык клиента (детект) — отдельно, на стороне моста; тут не выбирается.
// Состояние хранится per-conversation в localStorage; наверх отдаём через emit (родитель переводит входящие).
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { vOnClickOutside } from '@vueuse/components';
import {
  VIBEVOX_LANGUAGES,
  vibevoxFilterLanguages,
  vibevoxLangName,
  vibevoxLangEnglish,
} from 'dashboard/constants/vibevoxLanguages';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['change']);

const { locale } = useI18n();
const store = useStore();
const enabled = ref(false);
const targetLang = ref('en');
const open = ref(false);
const query = ref('');

const storeKey = computed(() => `vibevox_translate_${props.conversationId || 'x'}`);

const defaultLang = () => (locale.value || 'en').split('-')[0];

// Глобальный дефолт (per-оператор, этот браузер): «включать перевод во ВСЕХ новых диалогах».
const DEFAULT_KEY = 'vibevox_translate_default';
const applyDefault = ref(false);
const loadDefault = () => {
  try {
    const raw = localStorage.getItem(DEFAULT_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    /* ignore */
  }
  return null;
};
const saveDefault = () => {
  try {
    localStorage.setItem(
      DEFAULT_KEY,
      JSON.stringify({ enabled: applyDefault.value, lang: targetLang.value })
    );
  } catch (e) {
    /* ignore */
  }
};

// Аккаунт-дефолт перевода из настроек интеграции «VibeVox AI» (openai hook) — на ВЕСЬ аккаунт.
const accountDefault = () => {
  try {
    const apps = store.getters['integrations/getAppIntegrations'] || [];
    const openai = apps.find(a => a.id === 'openai');
    const s = openai?.hooks?.[0]?.settings || {};
    if (s.auto_translate) return { enabled: true, lang: s.op_lang || null };
  } catch (e) {
    /* ignore */
  }
  return null;
};

const emitState = () => {
  const payload = {
    conversationId: props.conversationId,
    enabled: enabled.value,
    targetLang: targetLang.value,
  };
  emit('change', payload);
  // Реактивность для рендера сообщений: входящий перевод слушает эту шину.
  try {
    emitter.emit('VIBEVOX_TRANSLATE_CHANGED', payload);
  } catch (e) {
    /* шина недоступна — не критично */
  }
};

// Конверсейшн-атрибут vibevox_translate читает мост (исходящий перевод оператор→клиент).
const syncConversationFlag = () => {
  if (!props.conversationId) return;
  store
    .dispatch('updateCustomAttributes', {
      conversationId: props.conversationId,
      customAttributes: {
        vibevox_translate: enabled.value,
        vibevox_op_lang: vibevoxLangEnglish(targetLang.value),
      },
    })
    .catch(() => {});
};

const persist = () => {
  try {
    localStorage.setItem(
      storeKey.value,
      JSON.stringify({ enabled: enabled.value, lang: targetLang.value })
    );
  } catch (e) {
    /* localStorage недоступен — не критично */
  }
};

const load = () => {
  const def = loadDefault();
  applyDefault.value = !!def?.enabled;
  // Язык: из глобального дефолта, иначе язык интерфейса оператора.
  targetLang.value =
    def?.lang && VIBEVOX_LANGUAGES.some(l => l.code === def.lang) ? def.lang : defaultLang();
  enabled.value = false;
  let hadPerConv = false;
  try {
    const raw = localStorage.getItem(storeKey.value);
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.enabled === 'boolean') {
        enabled.value = s.enabled;
        hadPerConv = true;
      }
      if (s.lang && VIBEVOX_LANGUAGES.some(l => l.code === s.lang)) targetLang.value = s.lang;
    }
  } catch (e) {
    /* битый json — игнор */
  }
  // Нет своего состояния у диалога → применяем дефолт: оператор (этот браузер) → аккаунт (хук).
  if (!hadPerConv) {
    if (def?.enabled) {
      enabled.value = true;
    } else {
      const acct = accountDefault();
      if (acct?.enabled) {
        enabled.value = true;
        if (acct.lang && VIBEVOX_LANGUAGES.some(l => l.code === acct.lang)) targetLang.value = acct.lang;
      }
    }
  }
  // Если перевод включён (свой или по дефолту) — сразу выставим конверсейшн-атрибут для моста.
  if (enabled.value) syncConversationFlag();
  emitState();
};

const toggle = () => {
  if (props.disabled) return;
  enabled.value = !enabled.value;
  persist();
  syncConversationFlag();
  emitState();
};

const filtered = computed(() => vibevoxFilterLanguages(query.value));
const currentName = computed(() => vibevoxLangName(targetLang.value));

const pick = code => {
  targetLang.value = code;
  open.value = false;
  query.value = '';
  persist();
  syncConversationFlag();
  if (applyDefault.value) saveDefault();
  emitState();
};

const closeDropdown = () => {
  open.value = false;
  query.value = '';
};

// Чекбокс «по умолчанию для всех новых диалогов» (per-оператор).
const toggleDefault = () => {
  applyDefault.value = !applyDefault.value;
  saveDefault();
};

onMounted(async () => {
  // Подгружаем интеграции, чтобы прочитать аккаунт-дефолт перевода из хука «VibeVox AI».
  try {
    await store.dispatch('integrations/get');
  } catch (e) {
    /* не критично — упадём на off/локальный дефолт */
  }
  load();
});
watch(() => props.conversationId, load);
</script>

<template>
  <div class="flex relative flex-shrink-0 items-center">
    <!-- Сегмент-тумблер «Перевод», стиль как EditorModeToggle -->
    <button
      type="button"
      :disabled="disabled"
      class="flex items-center p-1 h-8 transition-all border rounded-full bg-n-alpha-2 active:scale-[0.995] disabled:opacity-60"
      :title="
        enabled
          ? 'Перевод вкл: входящие → на ваш язык, ваши ответы → на язык клиента. Нажмите, чтобы выключить.'
          : 'Перевод выкл. Нажмите, чтобы включить двусторонний перевод.'
      "
      @click="toggle"
    >
      <span
        class="flex gap-1 items-center px-2 h-6 text-sm rounded-full transition-all duration-300"
        :class="enabled ? 'font-medium shadow-sm bg-n-solid-1 text-n-teal-11' : 'text-n-slate-11'"
      >
        <span class="i-lucide-languages size-3.5" />
        {{ enabled ? 'Перевод вкл' : 'Перевод выкл' }}
      </span>
    </button>

    <!-- Кнопка-пикер языка оператора (виден только при включённом переводе) -->
    <button
      v-if="enabled"
      type="button"
      class="flex gap-1 items-center px-2 ltr:ml-1 rtl:mr-1 h-8 text-sm rounded-full border bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-12"
      title="На какой язык переводить входящие для вас"
      @click="open = !open"
    >
      {{ currentName }}
      <span class="i-lucide-chevron-down size-3.5 text-n-slate-10" />
    </button>

    <!-- Выпадающий список с поиском (108 языков) -->
    <div
      v-if="enabled && open"
      v-on-click-outside="closeDropdown"
      class="absolute bottom-full mb-2 z-50 w-64 max-w-[calc(100vw-2rem)] ltr:left-0 rtl:right-0 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg overflow-hidden"
    >
      <div class="p-2 border-b border-n-weak">
        <input
          v-model="query"
          type="text"
          placeholder="Поиск языка…"
          class="w-full px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand"
          autofocus
        />
      </div>
      <ul class="overflow-y-auto py-1 max-h-64">
        <li v-if="!filtered.length" class="px-3 py-2 text-sm text-n-slate-10">
          Ничего не найдено
        </li>
        <li v-for="l in filtered" :key="l.code">
          <button
            type="button"
            class="flex justify-between items-center px-3 py-1.5 w-full text-sm text-left hover:bg-n-alpha-2"
            :class="l.code === targetLang ? 'text-n-teal-11 font-medium' : 'text-n-slate-12'"
            @click="pick(l.code)"
          >
            <span>{{ l.name }}</span>
            <span class="text-xs text-n-slate-10">{{ l.en }}</span>
          </button>
        </li>
      </ul>
      <!-- VibeVox: дефолт «включать перевод во всех новых диалогах» (per-оператор) -->
      <label
        class="flex gap-2 items-center px-3 py-2 text-sm border-t cursor-pointer select-none border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
        title="Новые диалоги будут открываться с включённым переводом на выбранный язык"
      >
        <input
          type="checkbox"
          class="accent-n-teal-9"
          :checked="applyDefault"
          @change="toggleDefault"
        />
        Включать во всех новых диалогах
      </label>
    </div>
  </div>
</template>
